import { ChallengeService } from '@duck/api-definitions'
import { CommandLineIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { useEffectAsync } from '../../hooks/useEffectAsync'
import { useAuth } from '@/hooks/useAuth'
import { apis } from '../../modules/apiClients'
import { useTar } from '@/modules'
import { ChallengeCardSkeleton } from '../ChallengesPage/ChallengeCardSkeleton'
import { AssignmentCard } from '../ChallengesPage'
import type { Assignment } from '../ChallengesPage/AssignmentCard'
import { NoDataEmptyState, LoginRequiredEmptyState } from '../EmptyStates'

export const AssignmentsPage = () => {
    const [assignments, setAssignments] = useState<Assignment[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [showLoading, setShowLoading] = useState(false)
    const { isAuthenticated } = useAuth()
    const { t } = useTar()

    useEffectAsync(async () => {
        if (isAuthenticated) {
            try {
                setIsLoading(true)
                const challengesResponse =
                    await apis.challengeService.getAvailableChallenges()
                const assignments =
                    challengesResponse.data.availableAssignments?.map(
                        (assignment: ChallengeService.AvailableAssignment) => ({
                            ...assignment,
                            icon: CommandLineIcon, // Default icon for now
                            estimatedTime: '30', // Default estimated time
                        })
                    ) || []
                setAssignments(assignments)
            } catch (error) {
                console.error('Failed to load assignments:', error)
            } finally {
                setIsLoading(false)
            }
        } else {
            setIsLoading(false)
        }
    }, [isAuthenticated])

    useEffect(() => {
        if (isLoading) {
            const timeout = setTimeout(() => {
                setShowLoading(true)
            }, 256)
            return () => clearTimeout(timeout)
        }
        setShowLoading(false)
    }, [isLoading])

    return (
        <div className="flex flex-1 w-full justify-center">
            <div className="flex-1 flex flex-col gap-12 mt-4">
                {/* Header Section */}
                <div className="space-y-8 flex-1">
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h1 className="text-3xl font-bold">
                                {t('partner.assignments')}
                            </h1>
                        </div>
                        <p className="text-muted-foreground text-lg leading-relaxed">
                            {assignments.length === 1
                                ? t('challenges.availableAssignmentsDescriptionSingular')
                                : t('challenges.availableAssignmentsDescriptionPlural', { count: assignments.length })}
                        </p>
                    </div>

                    {showLoading && isLoading && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <ChallengeCardSkeleton count={5} />
                        </div>
                    )}

                    <div className="flex flex-1 flex-col gap-12">
                        {isAuthenticated && assignments.length > 0 && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {assignments.map((assignment) => (
                                    <AssignmentCard
                                        key={assignment.assignmentId}
                                        assignment={assignment}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Empty State */}
                    {!isLoading && assignments.length === 0 && (
                        <>
                            {!isAuthenticated ? (
                                <LoginRequiredEmptyState />
                            ) : (
                                <NoDataEmptyState />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

